// Get file type based on extension
export const getFileType = (fileName) => {
  if (!fileName) return 'other';

  const ext = fileName.split('.').pop().toLowerCase();

  // Image files
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return 'image';
  } else if (['pdf'].includes(ext)) {
    return 'pdf';
  } else if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'wmv', 'flv'].includes(ext)) {
    return 'video';
  } else if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(ext)) {
    return 'audio';
  } else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(ext)) {
    return 'document';
  }

  return 'other';
};

// Get file icon based on type
export const getFileIcon = (type) => {
  switch (type) {
    case 'image':
      return { icon: '🖼️', color: 'text-green-500' };
    case 'pdf':
      return { icon: '📄', color: 'text-red-500' };
    case 'video':
      return { icon: '🎬', color: 'text-purple-500' };
    case 'audio':
      return { icon: '🎵', color: 'text-orange-500' };
    case 'document':
      return { icon: '📝', color: 'text-blue-500' };
    default:
      return { icon: '📁', color: 'text-gray-500' };
  }
};

// Compress image file
export const compressImageFile = async (file, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const newWidth = Math.round(img.width * quality);
        const newHeight = Math.round(img.height * quality);

        canvas.width = newWidth;
        canvas.height = newHeight;

        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: file.lastModified
            });
            resolve(compressedFile);
          } else {
            resolve(file); // Return original if compression fails
          }
        }, 'image/jpeg', quality);
      };

      img.onerror = () => {
        console.error('Error loading image for compression');
        resolve(file); // Return original on error
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      console.error('Error reading file for compression');
      resolve(file); // Return original on error
    };

    reader.readAsDataURL(file);
  });
};

// Compress multiple files
export const compressFiles = async (files) => {
  const compressedFiles = [];
  for (const file of files) {
    const fileType = getFileType(file.name);
    if (fileType === 'image') {
      const compressed = await compressImageFile(file);
      compressedFiles.push(compressed);
    } else {
      compressedFiles.push(file); // Don't compress non-image files
    }
  }
  return compressedFiles;
};

// Validate file size
export const validateFileSize = (file, maxSize = 10 * 1024 * 1024) => {
  if (!file) return { valid: false, error: 'No file provided' };

  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit`
    };
  }

  return { valid: true, error: null };
};

// Check if file type is allowed
export const isFileTypeAllowed = (file, allowedTypes = []) => {
  if (!file) return { valid: false, error: 'No file provided' };

  if (allowedTypes.length === 0) {
    return { valid: true, error: null };
  }

  const fileType = getFileType(file.name);
  const isAllowed = allowedTypes.includes(fileType);

  return {
    valid: isAllowed,
    error: isAllowed ? null : `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`
  };
};

// Get file URL with base URL
export const getFileUrl = (filePath, baseUrl) => {
  if (!filePath) return null;

  // Remove leading slash if present
  const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;

  return `${baseUrl}${cleanPath}`;
};

// Store upload error in localStorage
export const storeUploadError = (errorMessage) => {
  const errorData = {
    message: errorMessage,
    timestamp: Date.now()
  };
  localStorage.setItem('uploadError', JSON.stringify(errorData));
};

// Get stored upload error
export const getStoredUploadError = (expiryMs = 300000) => {
  const storedError = localStorage.getItem('uploadError');
  if (!storedError) return null;

  try {
    const errorData = JSON.parse(storedError);

    // Check if error is still valid (not expired)
    if (Date.now() - errorData.timestamp > expiryMs) {
      localStorage.removeItem('uploadError');
      return null;
    }

    return errorData;
  } catch (error) {
    console.error('Error parsing stored upload error:', error);
    localStorage.removeItem('uploadError');
    return null;
  }
};

// Clear stored upload error
export const clearStoredUploadError = () => {
  localStorage.removeItem('uploadError');
};
